// Every call to the API goes through here: it attaches the token and turns an error response
// into something the UI can show as-is — the server already writes its messages in Swedish.

import type { ApiErrorDto } from "../types/index.ts";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /** For a refused form: the field the message is about. */
    readonly field: string | null = null,
  ) {
    super(message);
  }
}

// Empty locally, where the dev server proxies to the API; the API's own origin when deployed.
const apiBase = import.meta.env.VITE_API_URL ?? "";

/** A path on the API as an address the browser can load — for a frame or a link, not only fetch. */
export const apiUrl = (path: string): string => `${apiBase}${path}`;

let getToken: () => Promise<string | null> = async () => null;

export function setTokenSource(source: () => Promise<string | null>): void {
  getToken = source;
}

export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const token = await getToken();
  const headers = new Headers(init.headers);
  if (token) headers.set("authorization", `Bearer ${token}`);
  if (init.body && !headers.has("content-type")) headers.set("content-type", "application/json");

  let response: Response;
  try {
    response = await fetch(apiUrl(path), { ...init, headers });
  } catch {
    throw new ApiError(0, "network", "Det gick inte att nå Sortera Factory. Kontrollera din uppkoppling.");
  }
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as Partial<ApiErrorDto> | null;
    throw new ApiError(response.status, body?.error ?? "http", body?.message ?? "Något gick fel. Försök igen.", body?.field ?? null);
  }
  return response;
}

export async function apiJson<T>(path: string, init?: RequestInit): Promise<T> {
  return (await (await apiFetch(path, init)).json()) as T;
}
